import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import lodashCompact from 'lodash/fp/compact';

import {
  LEAD_STATUS_LEASE_SIGNED,
} from 'common/leadConstants';

import {
  LEASE_STATUS_DRAFT,
  LEASE_STATUS_SIGNED,
} from 'common/financeConstants';

import {
  calculateCommissionAmount,
} from 'common/financeHelpers';

import {
  SR_API_ACTION_APARTMENT_LOCATOR_SUBMIT_APARTMENT_LEASE,
  SR_API_ACTION_APARTMENT_LOCATOR_GET_APARTMENT_LEASES_BY_RENTER_USER_ID,
} from 'api/actionTypes';

import ButtonDeprecated, {
  BUTTON_THEME_BLUE,
  BUTTON_THEME_WHITE,
} from 'client/components/ButtonDeprecated';
import SrFormikField from 'client/components/SrFormikField';

import branch from 'client/core/branch';
import momentWithAustinTimezone from 'common/util/momentWithAustinTimezone';
import { createFormValidationFn, createInitialValues } from 'client/helpers/formikHelpers';
import withApiState from 'client/helpers/withApiState';
import ApartmentLocatorActions from 'client/actions/ApartmentLocator';

const FORM_ACTION_TYPE_SAVE_DRAFT = 'FORM_ACTION_TYPE_SAVE_DRAFT';
const FORM_ACTION_TYPE_UPDATE = 'FORM_ACTION_TYPE_UPDATE';
const FORM_ACTION_TYPE_SUBMIT = 'FORM_ACTION_TYPE_SUBMIT';

const formDateFormat = 'MMM DD, YYYY [at] h:mm A';

function ensureNumberOrNull(value) {
  if (typeof value !== 'number' || isNaN(value)) {
    return null;
  }
  return value;
}

function formatSubmitLeaseFormError(apiStateError) {
  if (apiStateError.inner) {
    return apiStateError.inner.message;
  }

  return apiStateError.message;
}

function getFormFields({ apartmentNames, onApartmentNameChange }) {
  return [
    {
      id: 'startDate',
      title: 'lease start date',
      type: 'date_picker',
      properties: {
        placeholder: 'MM/DD/YYYY',
        dateFormat: 'MM/DD/YYYY',
      },
      validations: ['required'],
    },
    {
      id: 'term',
      title: 'lease term (months)',
      type: 'short_text',
      properties: {
        pattern: '[0-9]*',
        placeholder: '12',
        onlyAcceptNumbers: true,
      },
      validations: ['required'],
    },
    {
      id: 'rentPrice',
      title: 'rent price ($)',
      type: 'short_text',
      properties: {
        pattern: '[0-9]*',
        placeholder: '1250',
        onlyAcceptNumbers: true,
      },
      validations: ['required'],
    },
    {
      id: 'commissionPercentage',
      title: 'percentage commission (%)',
      type: 'percentage',
      properties: {
        pattern: '[0-9]*',
        placeholder: '100',
        onlyAcceptNumbers: true,
      },
    },
    {
      id: 'commissionFlatRate',
      title: 'flat rate commission ($$) (optional)',
      type: 'short_text',
      properties: {
        pattern: '[0-9]*',
        placeholder: '500',
        onlyAcceptNumbers: true,
      },
    },
    {
      id: 'apartmentName',
      title: 'apartment name',
      type: 'autocomplete',
      properties: {
        placeholder: 'The Bowie',
        choices: apartmentNames || [],
        onChange: onApartmentNameChange,
      },
      validations: ['required'],
    },
    {
      id: 'apartmentUnit',
      title: 'apartment unit',
      type: 'short_text',
      properties: {
        placeholder: '204',
      },
      validations: ['required'],
    },
    {
      id: 'invoicePayoutPolicy',
      title: 'invoice payout policy (in days)',
      type: 'short_text',
      properties: {
        pattern: '[0-9]*',
        placeholder: '45',
        onlyAcceptNumbers: true,
      },
      validations: ['required'],
    },
    {
      id: 'apartmentAgentName',
      title: 'name of agent you toured with (optional)',
      type: 'short_text',
      properties: {
        placeholder: 'Agent Full Name',
      },
    },
    {
      id: 'apartmentOfficePhone',
      type: 'hidden',
    },
    {
      id: 'apartmentEmail',
      title: 'email for invoice',
      type: 'short_text',
      properties: {
        placeholder: 'jsmith@apartment.com',
      },
      validations: ['email'],
    },
    {
      id: 'notes',
      title: 'collections notes (optional)',
      properties: {
        placeholder: 'Write tips for collections here...',
      },
      type: 'long_text',
    },
    {
      id: 'listingId',
      type: 'hidden',
    },
    {
      id: 'apartmentLeaseId',
      type: 'hidden',
    },
  ];
}

@withApiState(({ lead }) => ({
  submitApartmentLeaseApiState: {
    action: SR_API_ACTION_APARTMENT_LOCATOR_SUBMIT_APARTMENT_LEASE,
  },
  loadApartmentLeaseApiState: {
    action: SR_API_ACTION_APARTMENT_LOCATOR_GET_APARTMENT_LEASES_BY_RENTER_USER_ID,
    queryId: `${lead.id}`,
  },
}))
@branch({
  apartmentsList: ['apartmentLocator', 'apartmentsList'],
  apartmentLeasesByUserId: ['apartmentLocator', 'apartmentLeasesByUserId'],
})
class SrAgentLeadLeaseForm extends Component {
  static propTypes = {
    dispatch: PropTypes.func.isRequired,
    submitApartmentLeaseApiState: PropTypes.object.isRequired,
    loadApartmentLeaseApiState: PropTypes.object.isRequired,
    reloadTable: PropTypes.func.isRequired,
    lead: PropTypes.object.isRequired,
    showSubmitLeaseFormMessage: PropTypes.bool.isRequired,
    apartmentsList: PropTypes.array,
    apartmentLeasesByUserId: PropTypes.object.isRequired,
  };

  constructor() {
    super();

    this.state = {
      formActionType: null,
    };

    this._isUnmounting = false;
  }

  componentDidMount() {
    const { lead, dispatch } = this.props;
    dispatch(ApartmentLocatorActions.getApartmentsList);
    dispatch(ApartmentLocatorActions.loadApartmentLeaseByRenterUserId, {
      renterUserId: lead.renterUserId,
    });
  }

  componentWillUnmount() {
    this._isUnmounting = true;
  }

  _onSaveOrSubmitForm = (values) => {
    const { lead, reloadTable, dispatch } = this.props;
    const { formActionType } = this.state;

    const apartmentLease = { ...values };
    Object.keys(values).forEach((key) => {
      if (!lead[key] && !values[key]) {
        delete apartmentLease[key];
      }
    });

    apartmentLease.commissionPercentage = ensureNumberOrNull(
      apartmentLease.commissionPercentage
    );
    apartmentLease.commissionFlatRate = ensureNumberOrNull(
      apartmentLease.commissionFlatRate
    );

    const commissionFields = {};
    if (
      apartmentLease.rentPrice
      && (
        apartmentLease.commissionPercentage
        || apartmentLease.commissionFlatRate
      )
    ) {
      const invoiceCommissionAmountCalculation = calculateCommissionAmount({
        rentPrice: apartmentLease.rentPrice,
        commissionPercentage: apartmentLease.commissionPercentage,
        commissionFlatRate: apartmentLease.commissionFlatRate,
      });
      commissionFields.commissionAmount = invoiceCommissionAmountCalculation.commissionAmount;
      commissionFields.commissionType = invoiceCommissionAmountCalculation.commissionType;
    }

    apartmentLease.renterUserId = lead.renterUserId;

    const leaseStatus = formActionType === FORM_ACTION_TYPE_SAVE_DRAFT
      ? LEASE_STATUS_DRAFT
      : LEASE_STATUS_SIGNED;

    dispatch(ApartmentLocatorActions.submitApartmentLease, {
      ...apartmentLease,
      ...commissionFields,
      notes: lodashCompact(Array.isArray(apartmentLease.notes)
        ? apartmentLease.notes
        : [apartmentLease.notes]),
      status: leaseStatus,
    })
      .then(() => {
        if (leaseStatus === LEASE_STATUS_SIGNED && lead.status !== LEAD_STATUS_LEASE_SIGNED) {
          return dispatch(ApartmentLocatorActions.updateLeadStatus, {
            apartmentLocatorUserId: lead.apartmentLocatorUserId,
            renterUserId: lead.renterUserId,
            fromLeadStatus: lead.status,
            toLeadStatus: LEAD_STATUS_LEASE_SIGNED,
          });
        }
      })
      .then(reloadTable);
  };

  render() {
    const {
      submitApartmentLeaseApiState,
      loadApartmentLeaseApiState,
      lead,
      showSubmitLeaseFormMessage,
      apartmentsList,
      apartmentLeasesByUserId,
    } = this.props;

    const existingApartmentLease = apartmentLeasesByUserId[lead.renterUserId];
    const existingApartmentLeaseIsDraft = (
      existingApartmentLease
      && existingApartmentLease.status === LEASE_STATUS_DRAFT
    );

    const formFields = getFormFields({
      apartmentLeaseId: existingApartmentLease
        ? existingApartmentLease.apartmentLeaseId
        : null,
      apartmentNames: (apartmentsList || []).map(({ apartmentName }) => apartmentName),
      onApartmentNameChange({ newValue, formikBag }) {
        const apartmentOption = (apartmentsList || []).find((apartmentOption) => {
          return (
            newValue
            && apartmentOption.apartmentName.toLowerCase() === newValue.toLowerCase()
          );
        });
        formikBag.setFieldValue(
          'listingId',
          apartmentOption && apartmentOption.id || null,
        );
        if (apartmentOption && apartmentOption.apartmentEmail) {
          formikBag.setFieldValue(
            'apartmentEmail',
            apartmentOption && apartmentOption.apartmentEmail,
          );
        }
        formikBag.setFieldValue(
          'apartmentOfficePhone',
          apartmentOption && apartmentOption.apartmentPhone || null,
        );
      },
    });

    if (loadApartmentLeaseApiState.inProgress) {
      return (
        <div className='loading-gif' />
      );
    }

    const initialValues = createInitialValues({
      formFields: formFields,
      defaultValues: existingApartmentLease || {},
    });

    initialValues.startDate = initialValues.startDate
      ? momentWithAustinTimezone(initialValues.startDate)
      : '';

    return (
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validateOnChange={false}
        validate={(values) => {
          const { formActionType } = this.state;
          if (formActionType === FORM_ACTION_TYPE_SAVE_DRAFT) {
            return {};
          }
          return createFormValidationFn(formFields)(values);
        }}
        onSubmit={this._onSaveOrSubmitForm}
        render={(formikBag) => {
          return (
            <form>
              {showSubmitLeaseFormMessage && (
                <div className='formHeadlineContainer'>
                  <span className='formHeadline'>
                    Submit the lease info below to move this lead to 'Signed'
                  </span>
                </div>
              )}

              {formFields.map((formField) => (
                <SrFormikField
                  key={formField.id}
                  formField={formField}
                  formikBag={formikBag}
                />
              ))}
              {submitApartmentLeaseApiState.error && (
                <div className='mainMessage formMessage error'>
                  <span>{formatSubmitLeaseFormError(submitApartmentLeaseApiState.error)}</span>
                </div>
              )}
              <div className='bottomActionsContainer'>
                {(!existingApartmentLease || existingApartmentLeaseIsDraft) && (
                  <ButtonDeprecated
                    theme={BUTTON_THEME_WHITE}
                    onClick={
                      () => this.setState(
                        { formActionType: FORM_ACTION_TYPE_SAVE_DRAFT },
                        formikBag.handleSubmit,
                      )
                    }
                  >
                    {submitApartmentLeaseApiState.inProgress ? 'saving...' : 'save'}
                  </ButtonDeprecated>
                )}

                {(!existingApartmentLease || existingApartmentLeaseIsDraft) && (
                  <ButtonDeprecated
                    theme={BUTTON_THEME_BLUE}
                    onClick={
                      () => this.setState({ formActionType: FORM_ACTION_TYPE_SUBMIT }, formikBag.handleSubmit)
                    }
                  >
                    {submitApartmentLeaseApiState.inProgress ? 'submitting...' : 'submit lease info'}
                  </ButtonDeprecated>
                )}

                {existingApartmentLease && !existingApartmentLeaseIsDraft && (
                  <ButtonDeprecated
                    theme={BUTTON_THEME_BLUE}
                    onClick={
                      () => this.setState({ formActionType: FORM_ACTION_TYPE_UPDATE }, formikBag.handleSubmit)
                    }
                  >
                    {submitApartmentLeaseApiState.inProgress ? 'updating...' : 'update lease info'}
                  </ButtonDeprecated>
                )}

                {existingApartmentLease && !existingApartmentLeaseIsDraft && (
                  <div className='timestampsContainer'>
                    <div className='timestamp'>
                      <span>Submitted on </span>
                      <span className='bold'>
                        {momentWithAustinTimezone(
                          existingApartmentLease.createdDate
                        ).format(formDateFormat)}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </form>
          );
        }}
      />
    );
  }
}

SrAgentLeadLeaseForm.displayName = 'SrAgentLeadLeaseForm';

export default SrAgentLeadLeaseForm;
